import {
  Control,
  ControllerRenderProps,
  FieldValues,
  Path,
} from "react-hook-form";
import { FormControl, FormField, FormItem, FormLabel } from "./Form";
import { cn } from "../../../utils";
import { RadioGroup, RadioGroupItem } from "../RadioGroup";
import { checkboxContainerClassName } from "../../../styles/input";
import { ComponentProps, ReactNode } from "react";

export type RadioItem = {
  label: string;
  value: string;
  disabled?: boolean;
  component?: ReactNode;
};

type FormRadioFieldProps<TForm extends FieldValues> = {
  variant?: ComponentProps<typeof RadioGroup>["variant"];
  field: ControllerRenderProps<TForm, Path<TForm>>;
  radioItems: RadioItem[];
  containerClassName?: string;
  className?: string;
  itemClassName?: string;
  itemContainerClassName?: string;
  innerItemClassName?: string;
  labelClassName?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
};

type FormRadioProps<TForm extends FieldValues> = {
  control: Control<TForm>;
  name: Path<TForm>;
} & Omit<FormRadioFieldProps<TForm>, "field">;

export function FormRadio<TForm extends FieldValues>({
  control,
  name,
  ...props
}: FormRadioProps<TForm>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => <FormRadioField {...props} field={field} />}
    />
  );
}

function FormRadioField<TForm extends FieldValues>({
  variant,
  field,
  radioItems,
  className,
  containerClassName,
  itemClassName,
  itemContainerClassName,
  innerItemClassName,
  labelClassName = "",
  onChange,
  disabled,
}: FormRadioFieldProps<TForm>) {
  const { value, onChange: onValueChange } = field;

  const handleChange = (value: string) => {
    onValueChange(value);
    if (onChange) onChange(value);
  };

  const renderContent = (item: RadioItem) => (
    <FormItem
      className={cn(checkboxContainerClassName, itemClassName)}
      key={item.value}
    >
      <FormControl>
        <RadioGroupItem
          variant={variant}
          value={item.value}
          label={item.label}
          className={innerItemClassName}
          disabled={item.disabled}
        />
      </FormControl>

      {item.label && variant !== "buttons" && variant !== "cards" && (
        <FormLabel className={labelClassName}>{item.label}</FormLabel>
      )}
    </FormItem>
  );

  return (
    <FormItem className={cn("size-full flex-1", className)}>
      <FormControl>
        <RadioGroup
          variant={variant}
          onValueChange={handleChange}
          defaultValue={value}
          value={String(value)}
          className={containerClassName}
          disabled={disabled}
        >
          {radioItems.map((item) => {
            if (item.component) {
              return (
                <div className={cn(itemContainerClassName)} key={item.value}>
                  {renderContent(item)}
                  {item.component}
                </div>
              );
            }

            return renderContent(item);
          })}
        </RadioGroup>
      </FormControl>
    </FormItem>
  );
}

export default FormRadio;
